import classNames from 'classnames';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import Helmet from 'react-helmet';

import * as fixedImageStyles from './fixed-image.module.scss';

function getObjectPosition(imageDetails: Queries.ImageDetails) {
  switch (imageDetails.horizontalFocus) {
    case 'start':
      return '0% 50%';
    case 'left':
      return '25% 50%';
    case 'right':
      return '75% 50%';
    case 'end':
      return '100% 50%';
    default:
      return '50% 50%';
  }
}

export type FixedImageProps = {
  imageDetails: Queries.ImageDetails | undefined;
  className?: string;
  loading?: 'eager' | 'lazy';
  onImageLoaded?: () => void;
  enablePreload?: boolean;
};

const FixedImage = forwardRef<HTMLImageElement, FixedImageProps>(
  (
    { imageDetails, className, loading, onImageLoaded = () => undefined, enablePreload = false },
    ref
  ) => {
    const innerRef = useRef<HTMLImageElement | null>(null);
    useImperativeHandle(ref, () => innerRef.current!, []);

    useEffect(() => {
      if (innerRef.current?.complete) {
        onImageLoaded?.();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO why are the dependencies not exhaustive?
    }, []);

    if (!imageDetails?.imagePath) {
      return null;
    }

    return (
      <>
        {enablePreload && (
          <Helmet>
            <link rel="preload" fetchPriority="high" as="image" href={imageDetails.imagePath} />
          </Helmet>
        )}
        <img
          ref={innerRef}
          src={imageDetails.imagePath}
          alt={imageDetails?.alternateText ?? ''}
          className={classNames(fixedImageStyles.image, className)}
          style={
            {
              '--fixed-image-object-position': getObjectPosition(imageDetails),
            } as React.CSSProperties
          }
          loading={loading}
          onLoad={onImageLoaded}
        />
      </>
    );
  }
);

FixedImage.displayName = 'FixedImage';

export default FixedImage;
